/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  memo,
  useCallback,
  useEffect,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {Entity, SSOConfiguration} from '@ion/core/src/types/entities';
import {useSSOConfiguration} from '@ion/query/src/hooks/useSSOConfiguration';
import {useUpdateSSOConfiguration} from '@ion/query/src/hooks/useUpdateSSOConfiguration';
import {errorEditOptions} from '../../../prompts/constants';
import {toggleUserPrompt} from '../../../store/actions/user-prompt';
import {saveSSOAccount} from './saveSSOAccount';
import SSOEditor from './SSOEditor';
import {editSSOConfig} from './state';
import {editSSOSelectedSelector} from './state-selectors';

interface EditSSOEditorProps {
  refreshInitialization?: Function;
  isView?: boolean;
};

/**
 * This dialog for a user to update a SSO Configuration.
 *
 * ```typescript
 *
 *  import EditSSOEditor from '@ion/applications/src/pages/admin/accounts/EditSSOEditor';
 *
 *  <EditSSOEditor refreshInitialization={refreshInitialization}
 *    isView={!hasUpdatePermission} />
 *
 * ```
 */
export const EditSSOEditor = ({refreshInitialization, isView = false}: EditSSOEditorProps) => {
  const dispatch                        = useDispatch();
  const selectedAccount                 = useSelector(editSSOSelectedSelector);
  const [clear, setClear]               = useState(false);
  const [ssoConfigObj, setSSOConfigObj] = useState(null);
  const ssoConfigId                     = entityId(selectedAccount?.ssoConfig, false);
  const {ssoConfig, isConfigLoading}    = useSSOConfiguration(ssoConfigId);

  const {
    update,
    isSuccess,
    isError,
    isLoading
  } = useUpdateSSOConfiguration();

  useEffect(() => {
    if (isSuccess) {
      const accObj = {
        ...selectedAccount,
        ssoMode : ssoConfigObj?.sso_mode
      };
      saveSSOAccount(dispatch, {
        account   : accObj,
        refreshInitialization,
        onUpdated : function() {
          dispatch(editSSOConfig(null, false));
          setClear(true);
        }
      });
    }
    if (isError) {
      dispatch(toggleUserPrompt(errorEditOptions(Entity.ssoConfiguration)));
    }
  }, [isSuccess, isError]);

  const onSave = useCallback(function(ssoConfigData: SSOConfiguration): void {
    setSSOConfigObj(ssoConfigData);
    const editObj = {...ssoConfig, ...ssoConfigData};
    delete editObj.sso_mode;
    update(editObj);
  }, [ssoConfig]);

  const onClose = useCallback(function(): void {
    setSSOConfigObj(null);
    dispatch(editSSOConfig(null, false));
    setClear(true);
  }, []);

  if (!isConfigLoading) {
    return (
      <SSOEditor onSave={onSave}
        onClose={onClose}
        clear={clear}
        refreshInitialization={refreshInitialization}
        ssoConfiguration={ssoConfig}
        selectedAccount={selectedAccount}
        isLoading={isLoading}
        isView={isView} />
    );
  }
};

export default memo(EditSSOEditor);
